import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  useDeletePatientDocumentMutation,
  usePatientDocumentDownloadMutation,
} from '@common/cache';
import { DocumentFile } from '@common/types';
import { FilePreviewContent } from '@components';

export const useDocuments = () => {
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [fileIdToDelete, setFileIdToDelete] = useState('');
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<FilePreviewContent | null>(
    null,
  );
  const [selectedFile, setSelectedFile] = useState<DocumentFile | null>(null);

  const [downloadFile, setDownloadFile] = useState(false);

  const { mutateAsync: deleteFileAsync, isPending: isDeleting } =
    useDeletePatientDocumentMutation();

  const {
    data: file,
    isPending: isFileDownloading,
    mutateAsync,
    reset,
  } = usePatientDocumentDownloadMutation();

  useEffect(() => {
    if (!previewFile && file) {
      const urlCreator = window.URL || window.webkitURL;
      const fileUrl = urlCreator.createObjectURL(file);
      setPreviewFile({
        type: file.type,
        content: fileUrl,
      });
    }

    if (file && downloadFile) {
      const url = URL.createObjectURL(file);

      const a = document.createElement('a');
      a.href = url;
      a.download = selectedFile?.file_name || '';

      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }

      a.addEventListener('click', handleOnDownload, false);
      a.click();
      setDownloadFile(false);
    }
  }, [file, downloadFile]);

  const handelOpenFileUploadModal = () => {
    setIsFileUploadModalOpen(true);
  };

  const handelCloseFileUploadModal = () => {
    setIsFileUploadModalOpen(false);
  };

  const handleOpenConfirmModal = (id: string) => {
    setIsConfirmModalOpen(true);
    setFileIdToDelete(id);
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setFileIdToDelete('');
  };

  const handleDeleteFile = async () => {
    try {
      await deleteFileAsync(fileIdToDelete);
      toast.success('File deleted!');
      handleCloseConfirmModal();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleOpenPreviewFileModal = async (fileDetails: DocumentFile) => {
    setIsPreviewModalOpen(true);
    setSelectedFile(fileDetails);

    const loadingToast = toast.loading('Open  file...', {
      isLoading: true,
      autoClose: 500,
    });

    try {
      await mutateAsync(fileDetails.id);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleClosePreviewFileModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewFile(null);
    reset();
  };

  return {
    isFileUploadModalOpen,
    handelOpenFileUploadModal,
    handelCloseFileUploadModal,
    handleDeleteFile,
    isDeleting,
    isConfirmModalOpen,
    handleCloseConfirmModal,
    handleOpenConfirmModal,
    handleOpenPreviewFileModal,
    handleClosePreviewFileModal,
    isPreviewModalOpen,
    previewFile,
    selectedFile,
    isFileDownloading,
  };
};
